// src/components/AssignMemberPhotos.js
import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate, useLocation } from 'react-router-dom';
import { db, storage } from '../firebase';
import { doc, onSnapshot, collection, query, where, orderBy, getDocs, updateDoc } from 'firebase/firestore';
import { ref, listAll, getDownloadURL, getMetadata } from 'firebase/storage';
import { Spinner, Alert } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faArrowLeft,
    faUsers,
    faImages,
    faCheck,
    faInfoCircle,
    faArrowRight,
    faUserCircle
} from '@fortawesome/free-solid-svg-icons';
import { useAuth } from '../context/AuthContext';
import './AssignMemberPhotos.css';

const DEFAULT_MEMBER_PHOTO_URL = '/Person.png';

function AssignMemberPhotos() {
    const { familyId } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const fromPhotoUpload = location.state?.fromPhotoUpload || false;
    const { loadingAuth } = useAuth();

    const [family, setFamily] = useState(null);
    const [members, setMembers] = useState([]);
    const [memberPhotos, setMemberPhotos] = useState({});
    const [extractedFamilyPhotos, setExtractedFamilyPhotos] = useState([]);

    const [selectedMemberId, setSelectedMemberId] = useState(null);
    const [selectedPhoto, setSelectedPhoto] = useState(null);

    const [loadingFamily, setLoadingFamily] = useState(true);
    const [loadingMembers, setLoadingMembers] = useState(true);
    const [loadingExtractedPhotos, setLoadingExtractedPhotos] = useState(true);
    const [isAssigning, setIsAssigning] = useState(false);
    const [isExtracting, setIsExtracting] = useState(false);
    const [lastFacesExtractedAt, setLastFacesExtractedAt] = useState(null);

    const [error, setError] = useState(null);
    const [successMessage, setSuccessMessage] = useState(null);

    useEffect(() => {
        if (loadingAuth || !familyId) {
            if (!loadingAuth && !familyId) {
                setError("Family ID is missing.");
                setLoadingFamily(false); setLoadingMembers(false); setLoadingExtractedPhotos(false);
            }
            return;
        }
        setLoadingFamily(true);

        const familyDocRef = doc(db, 'family', familyId);
        const unsubscribe = onSnapshot(familyDocRef, (docSnap) => {
            if (docSnap.exists()) {
                const data = docSnap.data();
                setFamily({ id: docSnap.id, ...data });

                // Track extraction status
                const imageUpdatedAt = data.imageUpdatedAt?.toMillis?.() || 0;
                const facesExtractedAt = data.facesExtractedAt?.toMillis?.() || 0;
                const EXTRACTION_TIMEOUT_MS = 10 * 60 * 1000; // 10 minutes
                setIsExtracting(
                    imageUpdatedAt > 0 &&
                    imageUpdatedAt > facesExtractedAt &&
                    (Date.now() - imageUpdatedAt) < EXTRACTION_TIMEOUT_MS
                );

                // If facesExtractedAt changed, re-fetch extracted photos
                const newFacesExtractedAt = data.facesExtractedAt?.toMillis?.() || null;
                setLastFacesExtractedAt(newFacesExtractedAt);

                setLoadingFamily(false);
            } else {
                setError("Family not found.");
                setLoadingFamily(false);
            }
        }, (err) => {
            console.error("Error listening to family data:", err);
            setError("Could not load family data. " + err.message);
            setLoadingFamily(false);
        });

        return () => unsubscribe();
    }, [familyId, loadingAuth]);

    useEffect(() => {
        if (!family || !family.orgId) {
            if (family === null && !loadingFamily) { setLoadingMembers(false); }
            return;
        }
        setLoadingMembers(true);
        const fetchMembers = async () => {
            try {
                const membersQuery = query(collection(db, 'member'), where('familyId', '==', family.id), where('orgId', '==', family.orgId), orderBy('name'));
                const memberSnapshot = await getDocs(membersQuery);

                const memberList = memberSnapshot.docs
                    .map(d => ({ ...d.data(), id: d.id }))
                    .filter(member => {
                        if (member.id && typeof member.id === 'string' && member.id.trim() !== '') {
                            return true;
                        }
                        console.warn("Filtered out member with invalid document ID:", member);
                        return false;
                    });

                setMembers(memberList);

                if (memberList.length > 0) {
                    const photoPromises = memberList.map(async (member) => {
                        if (member.photo && !member.photo.startsWith('http')) {
                            try {
                                const url = await getDownloadURL(ref(storage, member.photo));
                                return { memberId: member.id, url };
                            } catch (photoError) {
                                if (photoError.code !== 'storage/object-not-found') { console.warn(`Could not get photo URL for member ${member.name}: ${member.photo}`, photoError); }
                                return { memberId: member.id, url: DEFAULT_MEMBER_PHOTO_URL };
                            }
                        } else if (member.photo && member.photo.startsWith('http')) {
                            return { memberId: member.id, url: member.photo };
                        }
                        return { memberId: member.id, url: DEFAULT_MEMBER_PHOTO_URL };
                    });
                    const resolvedPhotos = await Promise.all(photoPromises);
                    setMemberPhotos(resolvedPhotos.reduce((acc, curr) => {
                        if (curr.memberId) {
                            acc[curr.memberId] = curr.url;
                        }
                        return acc;
                    }, {}));
                } else {
                    setMemberPhotos({});
                }
            } catch (err) {
                console.error("Error fetching members:", err);
                setError("Could not load family members. " + err.message);
            } finally { setLoadingMembers(false); }
        };
        fetchMembers();
    }, [family, loadingFamily]);

    useEffect(() => {
        if (!family || !family.orgId) {
            if (family === null && !loadingFamily) { setLoadingExtractedPhotos(false); }
            return;
        }
        setLoadingExtractedPhotos(true);
        const photosPath = `${family.orgId}/family/${family.id}/members/`;
        const listRef = ref(storage, photosPath);
        listAll(listRef)
            .then(async (res) => {
                const photoPromises = res.items.map(async (itemRef) => {
                    const [downloadUrl, metadata] = await Promise.all([
                        getDownloadURL(itemRef),
                        getMetadata(itemRef),
                    ]);
                    return {
                        path: itemRef.fullPath,
                        url: downloadUrl,
                        timeCreated: metadata.timeCreated ? new Date(metadata.timeCreated).getTime() : 0,
                    };
                });
                const photosData = await Promise.all(photoPromises);
                photosData.sort((a, b) => b.timeCreated - a.timeCreated);
                setExtractedFamilyPhotos(photosData);
            })
            .catch((err) => {
                if (err.code === 'storage/object-not-found') { setExtractedFamilyPhotos([]); }
                else { console.error("Error listing extracted photos:", err); setError("Could not load extracted photos. " + err.message); }
            })
            .finally(() => { setLoadingExtractedPhotos(false); });
    }, [family?.orgId, family?.id, lastFacesExtractedAt]);

    const handleMemberSelect = (memberId, memberName) => {
        if (memberId === null || typeof memberId === 'undefined' || memberId === '') {
            console.error("CRITICAL: handleMemberSelect called with invalid memberId for member:", memberName);
            setError("An internal error occurred: Attempted to select a member with an invalid ID.");
            return;
        }
        setSelectedMemberId(memberId);
        setSuccessMessage(null);
    };

    const handlePhotoSelect = (photoData) => {
        setSelectedPhoto(photoData);
        setSuccessMessage(null);
    };

    const handleAssignPhoto = async () => {
        if (!selectedMemberId || !selectedPhoto) {
            setError("Please select a member and a photo to assign.");
            return;
        }
        setIsAssigning(true); setError(null); setSuccessMessage(null);
        try {
            const memberDocRef = doc(db, 'member', selectedMemberId);
            await updateDoc(memberDocRef, { photo: selectedPhoto.path });
            setMembers(prevMembers => prevMembers.map(m => m.id === selectedMemberId ? { ...m, photo: selectedPhoto.path } : m));
            setMemberPhotos(prev => ({ ...prev, [selectedMemberId]: selectedPhoto.url }));
            setSuccessMessage(`Photo assigned successfully to ${members.find(m => m.id === selectedMemberId)?.name}!`);
            setSelectedMemberId(null);
            setSelectedPhoto(null);
        } catch (err) {
            console.error("Error assigning photo:", err);
            setError("Failed to assign photo. " + err.message);
        } finally { setIsAssigning(false); }
    };

    const getDynamicInstruction = () => {
        const selectedMemberName = selectedMemberId ? (members.find(m => m.id === selectedMemberId)?.name || "the selected member") : "";
        if (!selectedMemberId && !selectedPhoto) {
            return "Select a family member from the left, then choose a photo to assign.";
        } else if (selectedMemberId && !selectedPhoto) {
            return `${selectedMemberName} is selected. Now choose a photo from the grid.`;
        } else if (!selectedMemberId && selectedPhoto) {
            return "Photo selected. Now choose a family member to assign it to.";
        } else {
            return `Ready to assign photo to ${selectedMemberName}. Click "Assign Photo" to confirm.`;
        }
    };

    const selectedMember = selectedMemberId ? members.find(m => m.id === selectedMemberId) : null;

    // Loading state
    if (loadingAuth || loadingFamily) {
        return (
            <div className="assign-photos-page">
                <div className="assign-photos-loading">
                    <div className="skeleton-header"></div>
                    <div className="assign-photos-grid">
                        <div className="skeleton-card"></div>
                        <div className="skeleton-card-large"></div>
                    </div>
                </div>
            </div>
        );
    }

    // Error state - no family
    if (error && !family) {
        return (
            <div className="assign-photos-page">
                <div className="assign-photos-header">
                    <div className="container">
                        <div className="assign-photos-header-content">
                            <div className="assign-photos-header-left">
                                <Link to="/families" className="assign-photos-back-btn">
                                    <FontAwesomeIcon icon={faArrowLeft} />
                                </Link>
                                <h1 className="assign-photos-title">Assign Photos</h1>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="container">
                    <div className="assign-photos-content">
                        <Alert variant="danger">{error}</Alert>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="assign-photos-page">
            {/* Page Header */}
            <div className="assign-photos-header">
                <div className="container">
                    <div className="assign-photos-header-content">
                        <div className="assign-photos-header-left">
                            <Link to={`/families/${familyId}`} className="assign-photos-back-btn" title="Back to Family">
                                <FontAwesomeIcon icon={faArrowLeft} />
                            </Link>
                            <div>
                                <h1 className="assign-photos-title">Assign Member Photos</h1>
                                <p className="assign-photos-subtitle">
                                    <Link to={`/families/${familyId}`}>{family?.surname || 'Family'} Family</Link>
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="container">
                <div className="assign-photos-content">
                    {/* Success/Error Messages */}
                    {error && (
                        <Alert variant="danger" onClose={() => setError(null)} dismissible className="mb-3">
                            {error}
                        </Alert>
                    )}
                    {successMessage && (
                        <Alert variant="success" onClose={() => setSuccessMessage(null)} dismissible className="mb-3">
                            {successMessage}
                        </Alert>
                    )}

                    {/* Instruction Banner */}
                    <div className="assign-photos-instruction">
                        <div className="assign-photos-instruction-icon">
                            <FontAwesomeIcon icon={faInfoCircle} />
                        </div>
                        <p className="assign-photos-instruction-text">{getDynamicInstruction()}</p>
                    </div>

                    {/* Main Grid */}
                    <div className="assign-photos-grid">
                        {/* Members Section */}
                        <div className="assign-photos-members">
                            <div className="assign-photos-members-header">
                                <h3 className="assign-photos-members-title">
                                    <FontAwesomeIcon icon={faUsers} />
                                    Family Members
                                    <span className="assign-photos-members-count">{members.length}</span>
                                </h3>
                            </div>
                            <div className="assign-photos-members-body">
                                {loadingMembers ? (
                                    <div className="assign-photos-loading-state">
                                        <Spinner animation="border" size="sm" />
                                        <span>Loading members...</span>
                                    </div>
                                ) : members.length === 0 ? (
                                    <div className="assign-photos-empty">
                                        <div className="assign-photos-empty-icon">
                                            <FontAwesomeIcon icon={faUsers} />
                                        </div>
                                        <h4>No Members</h4>
                                        <p>No members found for this family.</p>
                                    </div>
                                ) : (
                                    members.map(member => (
                                        <div
                                            key={member.id}
                                            className={`member-select-item ${selectedMemberId === member.id ? 'selected' : ''}`}
                                            onClick={() => handleMemberSelect(member.id, member.name)}
                                        >
                                            <img
                                                src={memberPhotos[member.id] || DEFAULT_MEMBER_PHOTO_URL}
                                                alt={member.name || 'Member'}
                                                className="member-select-photo"
                                                onError={(e) => {
                                                    if (e.target.src !== DEFAULT_MEMBER_PHOTO_URL) {
                                                        e.target.onerror = null;
                                                        e.target.src = DEFAULT_MEMBER_PHOTO_URL;
                                                    }
                                                }}
                                            />
                                            <div className="member-select-info">
                                                <h4 className="member-select-name">{member.name || 'Unnamed'}</h4>
                                                <p className={`member-select-status ${member.photo ? 'has-photo' : ''}`}>
                                                    {member.photo ? 'Photo assigned' : 'No photo'}
                                                </p>
                                            </div>
                                            <div className="member-select-check">
                                                <FontAwesomeIcon icon={faCheck} />
                                            </div>
                                        </div>
                                    ))
                                )}
                            </div>
                        </div>

                        {/* Photos Section */}
                        <div className="assign-photos-available">
                            <div className="assign-photos-available-header">
                                <h3 className="assign-photos-available-title">
                                    <FontAwesomeIcon icon={faImages} />
                                    Available Photos
                                    <span className="assign-photos-available-count">{extractedFamilyPhotos.length}</span>
                                </h3>
                            </div>
                            <div className="assign-photos-available-body">
                                {isExtracting && (
                                    <div className="assign-photos-extracting-banner">
                                        <Spinner animation="border" size="sm" />
                                        <span>Extracting faces from the latest photo...</span>
                                    </div>
                                )}
                                {loadingExtractedPhotos ? (
                                    <div className="assign-photos-loading-state">
                                        <Spinner animation="border" size="sm" />
                                        <span>Loading photos...</span>
                                    </div>
                                ) : extractedFamilyPhotos.length === 0 ? (
                                    <div className="assign-photos-empty">
                                        <div className="assign-photos-empty-icon">
                                            <FontAwesomeIcon icon={faImages} />
                                        </div>
                                        <h4>No Photos Available</h4>
                                        <p>No extracted photos found. Upload a family photo and faces will be automatically extracted.</p>
                                    </div>
                                ) : (
                                    <div className="extracted-photos-grid">
                                        {extractedFamilyPhotos.map(photo => (
                                            <div
                                                key={photo.path}
                                                className={`extracted-photo-item ${selectedPhoto?.path === photo.path ? 'selected' : ''}`}
                                                onClick={() => handlePhotoSelect(photo)}
                                            >
                                                <img
                                                    src={photo.url}
                                                    alt="Extracted face"
                                                    onError={(e) => { e.target.closest('.extracted-photo-item').style.display='none'; }}
                                                />
                                                <div className="extracted-photo-check">
                                                    <FontAwesomeIcon icon={faCheck} />
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Action Bar */}
                    <div className="assign-photos-action-bar">
                        <div className="assign-photos-selection-info">
                            {(selectedMember || selectedPhoto) ? (
                                <div className="assign-photos-selection-preview">
                                    {selectedMember && (
                                        <div className="selection-preview-item">
                                            <img
                                                src={memberPhotos[selectedMember.id] || DEFAULT_MEMBER_PHOTO_URL}
                                                alt={selectedMember.name}
                                            />
                                            <span>{selectedMember.name}</span>
                                        </div>
                                    )}
                                    {selectedMember && selectedPhoto && (
                                        <FontAwesomeIcon icon={faArrowRight} className="selection-arrow" />
                                    )}
                                    {selectedPhoto && (
                                        <div className="selection-preview-item">
                                            <img src={selectedPhoto.url} alt="Selected" />
                                            <span>New photo</span>
                                        </div>
                                    )}
                                </div>
                            ) : (
                                <span style={{ color: '#64748b', fontSize: '0.9rem' }}>
                                    <FontAwesomeIcon icon={faUserCircle} style={{ marginRight: '0.5rem' }} />
                                    Select a member and photo to assign
                                </span>
                            )}
                        </div>
                        <div className="assign-photos-actions">
                            <button
                                className="assign-btn assign-btn-secondary"
                                onClick={() => navigate(`/families/${familyId}`)}
                            >
                                {fromPhotoUpload ? 'Do This Later' : 'Back to Family'}
                            </button>
                            <button
                                className="assign-btn assign-btn-primary"
                                disabled={!selectedMemberId || !selectedPhoto || isAssigning}
                                onClick={handleAssignPhoto}
                            >
                                {isAssigning ? (
                                    <>
                                        <Spinner as="span" animation="border" size="sm" />
                                        Assigning...
                                    </>
                                ) : (
                                    <>
                                        <FontAwesomeIcon icon={faCheck} />
                                        Assign Photo
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default AssignMemberPhotos;
